import { FC, ReactNode } from 'react';
import {
    vettedTitle,
    vettedSeller,
    vettedDescription,
    goldSellerTitle,
    goldSeller,
    goldSellerDescription,
    platinumSellerTitle,
    platinumSeller,
    diamondSellerTitle,
    diamondSeller,
    tieredSellerDescription,
    recognizedTitle,
    recognizedSeller,
    recognizedDescription,
} from '../messages/pdpDistinctionsMessages';
import { graphql, createFragmentContainer } from 'react-relay/legacy';

// components
import TopSellerIcon from 'dibs-icons/exports/Diamond';
import VettedIcon from 'dibs-icons/exports/VettedCheckmark';
import DiamondFilled from 'dibs-icons/exports/DiamondFilled';
import RecognizedIcon from 'dibs-icons/exports/RecognizedRibbon';

// styles
import styles from './PdpSharedDealerDistinction.scss';

// types
import { PdpSharedDealerDistinction_seller$data as Seller } from './__generated__/PdpSharedDealerDistinction_seller.graphql';

type Props = {
    seller: Seller;
    hideDescription?: boolean;
    useShortTitles?: boolean;
};

type CreateDealerDistinctionProps = {
    icon: ReactNode;
    title: ReactNode;
    description: ReactNode;
    hideDescription: boolean;
};

const createDealerDistinction = ({
    icon,
    title,
    description,
    hideDescription,
}: CreateDealerDistinctionProps): ReactNode => (
    <>
        <div className={styles.distinction}>
            {icon}
            <span className={styles.title}>{title}</span>
        </div>
        {!hideDescription && <div className={styles.description}>{description}</div>}
    </>
);

const PdpSharedDealerDistinctionComponent: FC<Props> = ({
    seller,
    hideDescription = false,
    useShortTitles = false,
}) => {
    const sellerTiers = seller?.sellerTiers;
    const isRewardsTier2 = seller?.isRewardsTier2;
    const isDistinguished = seller?.isDistinguished;
    const sellerTierDataFound = sellerTiers?.sellerTierDataNotFound;
    const isGoldSeller = !sellerTierDataFound && sellerTiers?.isGoldTier;
    const isPlatinumSeller = !sellerTierDataFound && sellerTiers?.isPlatinumTier;
    const distinctions = [];

    if (isDistinguished) {
        distinctions.push(
            createDealerDistinction({
                icon: <RecognizedIcon className={styles.icon} />,
                title: useShortTitles ? recognizedTitle : recognizedSeller,
                description: recognizedDescription,
                hideDescription,
            })
        );
    }

    if (isGoldSeller && !isRewardsTier2) {
        distinctions.push(
            createDealerDistinction({
                icon: <TopSellerIcon className={`${styles.icon} ${styles.goldDiamond}`} />,
                title: useShortTitles ? goldSellerTitle : goldSeller,
                description: goldSellerDescription,
                hideDescription,
            })
        );
    } else if (isPlatinumSeller && !isRewardsTier2) {
        distinctions.push(
            createDealerDistinction({
                icon: <TopSellerIcon className={`${styles.icon} ${styles.platinumDiamond}`} />,
                title: useShortTitles ? platinumSellerTitle : platinumSeller,
                description: tieredSellerDescription,
                hideDescription,
            })
        );
    } else if (isRewardsTier2) {
        distinctions.push(
            createDealerDistinction({
                icon: <DiamondFilled className={styles.icon} />,
                title: useShortTitles ? diamondSellerTitle : diamondSeller,
                description: tieredSellerDescription,
                hideDescription,
            })
        );
    } else if (!distinctions.length) {
        distinctions.push(
            createDealerDistinction({
                icon: <VettedIcon className={styles.icon} />,
                title: useShortTitles ? vettedTitle : vettedSeller,
                description: vettedDescription,
                hideDescription,
            })
        );
    }

    return (
        <>
            {distinctions.map((distinction, index) => (
                <div
                    key={`distinction-${index}`}
                    className={hideDescription ? '' : styles.container}
                >
                    {distinction}
                </div>
            ))}
        </>
    );
};

export const PdpSharedDealerDistinction = createFragmentContainer(
    PdpSharedDealerDistinctionComponent,
    {
        seller: graphql`
            fragment PdpSharedDealerDistinction_seller on Seller {
                isDistinguished
                isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
                sellerTiers {
                    isGoldTier
                    isPlatinumTier
                    sellerTierDataNotFound
                }
            }
        `,
    }
);
